import {
    HttpTransportType,
    type HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
} from '@microsoft/signalr';
import { SupportStep } from './SupportStep';
import {
    SupportSessionStatus,
    type SupportStepData,
    WorkflowStates,
} from './support.type';
import { type UserSessionResponseItem } from '@clients/SupportApi';
import {
    type AuthStore,
    type PosthogHandler,
    type AnalyticsStore,
} from '#imports';

export class SupportWorkFlow {
    private hubConnection: HubConnection | null = null;

    private steps: SupportStep[] = [];
    private stopPromise: Promise<void> | null = null;
    currentStep?: SupportStep;

    sessionName?: string;
    billCode?: string;
    status: SupportSessionStatus = SupportSessionStatus.PreRegister;
    readonly startedAt?: string;
    readonly expiresAt?: string;
    prevXplayerId?: string;
    selected = false;
    state: WorkflowStates = WorkflowStates.idle;
    private userId: number | undefined;
    constructor(
        readonly sessionId: number,
        readonly publicId: string,
        private _xplayerID: string,
        private baseUrl: string,
        private authStore: AuthStore,
        private posthog: PosthogHandler,
        private analyticsStore: AnalyticsStore,
        session: Omit<UserSessionResponseItem, 'sessionId' | 'status'> & {
            status?: SupportSessionStatus;
        }
    ) {
        this.sessionName = session.sessionName || undefined;
        this.billCode = session.billCode || undefined;
        this.status = session.status || SupportSessionStatus.PreRegister;
        this.startedAt = session.startedAt;
        this.expiresAt = session.expiresAt || undefined;
    }

    get stateTitle() {
        switch (this.status) {
            case SupportSessionStatus.Active:
                return 'درجریان';
            case SupportSessionStatus.Expired:
                return 'بسته شده';
            case SupportSessionStatus.Pending:
                return 'منتظر رسیدگی';
            case SupportSessionStatus.UnderReview:
                return 'در حال بررسی';
            default:
                return 'نامشخص';
        }
    }

    private registerData({
        sessionActivityPayloads,
        status,
        billCode,
        sessionName,
        resetCurrentStep,
    }: {
        sessionActivityPayloads: SupportStepData[];
        status?: SupportSessionStatus;
        sessionName?: string;
        billCode?: string;
        resetCurrentStep?: boolean;
    }) {
        this.state = WorkflowStates.loading;
        try {
            this.steps = sessionActivityPayloads.map(
                (stepData, index) => new SupportStep(stepData, index)
            );
            this.status = status || this.status;
            this.billCode = billCode || this.billCode;
            this.sessionName = sessionName || this.sessionName;
        } catch (e) {
            console.log(e);
        }
        if (
            resetCurrentStep &&
            this.status !== SupportSessionStatus.Pending &&
            this.status !== SupportSessionStatus.UnderReview
        ) {
            [this.currentStep] = this.steps;
        } else {
            this.currentStep = this.steps.at(-1);
        }
        this.state = WorkflowStates.idle;
    }

    async start() {
        this.state = WorkflowStates.loading;
        this.selected = true;
        this.hubConnection = new HubConnectionBuilder()
            .withUrl(`${this.baseUrl}/sessionhub`, {
                accessTokenFactory: () => this.sessionId.toString() ?? '',
                headers: {
                    'X-PlayerID': this._xplayerID ?? '',
                },
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect([5000, 5000, 5000, 5000])
            .build();
        this.hubConnection.onreconnecting(() => {
            this.state = WorkflowStates.reconnecting;
        });
        this.hubConnection.onclose(() => {
            this.state = WorkflowStates.closed;
        });
        this.hubConnection.onreconnected(() => {
            this.state = WorkflowStates.idle;
        });
        await this.posthog.loadPromise;
        await this.hubConnection.start();
        this.hubConnection.invoke(
            'SubscribeToSession',
            this.sessionId,
            this.publicId,
            this.posthog.featureFlags['support-pre-bill-select'] === 'test'
        );
        this.hubConnection.on('WaitingForAnswer', val =>
            this.registerData(val)
        );
    }

    async next(loggedIn?: boolean) {
        this.state = WorkflowStates.loading;
        if (loggedIn) {
            this.prevXplayerId = this._xplayerID;
            this._xplayerID = this.authStore.uuid || '-';
            this.userId = this.authStore.userInfo?.id;
        }
        try {
            const val = this.currentStep?.getFormData();
            const order = this.currentStep?.order ?? 4;
            const question = this.currentStep?.selectedQuestion;
            if (order < 3 && question) {
                this.analyticsStore.trackAll('support-question-submit', {
                    stepIndex: order,
                    questionType: question.questionOptionType,
                    questionTitle: question.title,
                    needLogin: question.needLogin,
                });
            }
            const data = JSON.stringify({
                ...val,
                userId: this.userId,
                lastPlayerID: this.prevXplayerId,
                playerID: this._xplayerID,
            });
            if (this.hubConnection?.state !== HubConnectionState.Connected) {
                return;
            }
            await this.hubConnection?.invoke('SendUserResponse', data);
        } catch (error: unknown) {
            if (error instanceof Error && error.cause === 'custom') {
                this.state = WorkflowStates.error;
                throw error;
            }
        }
    }

    prev() {
        const currentStepIndex = this.currentStep?.order;
        if (currentStepIndex) {
            this.currentStep = this.steps.at(currentStepIndex - 1);
        } else {
            this.stop();
        }
    }

    async stop() {
        if (this.stopPromise) {
            await this.stopPromise;
            return;
        }
        const connection = this.hubConnection;
        if (
            !connection ||
            connection.state === HubConnectionState.Disconnected
        ) {
            this.selected = false;
            this.state = WorkflowStates.idle;
            return;
        }
        this.state = WorkflowStates.loading;
        const stopPromise = async () => {
            try {
                connection.off('WaitingForAnswer');
                await connection.stop();
            } finally {
                this.selected = false;
                this.state = WorkflowStates.idle;
                if (this.hubConnection === connection) {
                    this.hubConnection = null;
                }
                this.stopPromise = null;
            }
        };
        this.stopPromise = stopPromise();
        await this.stopPromise;
    }

    async terminate(userRequest?: string) {
        const lazy = !!userRequest;
        if (!lazy) {
            // we are not expecting any result
            this.state = WorkflowStates.loading;
        }
        try {
            const val = this.currentStep?.getFormData();
            const data = JSON.stringify({
                ...val,
                Items: [{ key: 0, value: userRequest || null }],
                lastPlayerID: this.prevXplayerId,
                playerID: this._xplayerID,
                userId: this.userId,
            });
            if (this.hubConnection?.state !== HubConnectionState.Connected) {
                return;
            }
            await this.hubConnection?.invoke('SendUserResponse', data);
        } catch (error: unknown) {
            this.state = WorkflowStates.error;
            if (error instanceof Error && error.cause === 'custom') {
                throw error;
            }
        }
    }

    get welcomeText() {
        return this.currentStep?.welcomeText;
    }
}
